import React, {
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import CreditBalanceCard from '../components/credits/CreditBalanceCard';
import './Credits.css';

const API_BASE = (
  process.env.REACT_APP_API_URL || 'http://localhost:5000/api'
).replace(/\/+$/, '');

const PURCHASE_STATUSES = new Set([
  'pending_payment',
  'payment_uploaded',
  'payment_verified',
  'rejected'
]);

const STATUS_LABELS = {
  payment_uploaded: 'Awaiting verification',
  payment_verified: 'Payment verified',
  pending_payment: 'Awaiting payment',
  rejected: 'Payment rejected',
  cancelled: 'Cancelled'
};

const STATUS_CLASSES = {
  completed: 'completed',
  pending: 'pending',
  failed: 'failed',
  refunded: 'refunded',
  cancelled: 'cancelled',
  reversed: 'reversed',
  payment_verified: 'completed',
  payment_uploaded: 'pending',
  pending_payment: 'pending',
  rejected: 'failed'
};

const readable = value =>
  String(value || '').replace(/_/g, ' ');

const statusLabel = status =>
  STATUS_LABELS[status] || readable(status) || 'Unknown';

const formatDate = value => {
  if (!value) return '—';

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleDateString();
};

const formatAmount = purchase =>
  `${purchase.currency || 'USD'} ${Number(
    purchase.amount || 0
  ).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;

const request = async (path, token, signal) => {
  const response = await fetch(`${API_BASE}${path}`, {
    headers: {
      Authorization: `Bearer ${
        token || localStorage.getItem('marilink_token') || ''
      }`
    },
    signal,
    cache: 'no-store'
  });

  const data = await response.json().catch(() => null);

  if (!response.ok || !data?.success) {
    throw new Error(
      data?.message || 'Unable to load data. Please try again.'
    );
  }

  return data;
};

const StatusBadge = ({ status, purchase = false }) => (
  <span className={`cr-badge ${STATUS_CLASSES[status] || 'pending'}`}>
    {purchase ? statusLabel(status) : readable(status)}
  </span>
);

const PurchaseDetails = ({ purchaseId, token, onClose }) => {
  const dialogRef = useRef(null);
  const [purchase, setPurchase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const dialog = dialogRef.current;

    if (dialog && !dialog.open) {
      dialog.showModal();
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    setLoading(true);
    setError('');
    setPurchase(null);

    request(
      `/credits/purchase/${encodeURIComponent(purchaseId)}`,
      token,
      controller.signal
    )
      .then(data => {
        if (!controller.signal.aborted) {
          if (!data.purchase) {
            throw new Error('Purchase details are unavailable.');
          }

          setPurchase(data.purchase);
        }
      })
      .catch(err => {
        if (!controller.signal.aborted) {
          setError(err.message);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, [purchaseId, token]);

  const proof = purchase?.paymentProof || {};
  const proofUrl = proof.imageUrl || '';
  const canShowProof =
    /^https:\/\//i.test(proofUrl) ||
    /^data:image\/(?:jpeg|jpg|png|webp);base64,/i.test(proofUrl);

  const rows = purchase
    ? [
        ['Purchase ID', purchase.purchaseId],
        ['Credits requested', purchase.credits],
        ['Amount', formatAmount(purchase)],
        ['Payment method', readable(purchase.paymentMethod)],
        ['Created', formatDate(purchase.createdAt)],
        ['Proof submitted', formatDate(proof.uploadedAt)],
        ['Verified', formatDate(proof.verifiedAt)],
        ['Completed', formatDate(purchase.completedAt)]
      ]
    : [];

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby="credit-purchase-title"
      onClose={onClose}
      onClick={event => {
        if (event.target === event.currentTarget) {
          dialogRef.current?.close();
        }
      }}
      style={{
        width: 'min(640px, calc(100vw - 32px))',
        maxHeight: '85vh',
        margin: 'auto',
        padding: 0,
        border: '1px solid rgba(148, 163, 184, 0.25)',
        borderRadius: 20,
        background: '#111827',
        color: '#f8fafc',
        boxShadow: '0 24px 80px rgba(0, 0, 0, 0.45)',
        overflowY: 'auto'
      }}
    >
      <div style={{ padding: 24 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
            marginBottom: 20
          }}
        >
          <h2
            id="credit-purchase-title"
            style={{ margin: 0, fontSize: 21 }}
          >
            Purchase details
          </h2>

          <button
            type="button"
            className="cr-btn cr-btn-secondary"
            onClick={() => dialogRef.current?.close()}
            autoFocus
          >
            Close
          </button>
        </div>

        {loading ? (
          <div className="cr-loading" role="status">
            <i className="fas fa-spinner fa-spin" aria-hidden="true" />
            <span style={{ marginLeft: 10 }}>Loading details...</span>
          </div>
        ) : error ? (
          <p role="alert" style={{ color: '#fca5a5' }}>
            {error}
          </p>
        ) : purchase ? (
          <>
            <StatusBadge status={purchase.status} purchase />

            {purchase.status === 'payment_uploaded' && (
              <p style={{ color: '#cbd5e1', lineHeight: 1.6 }}>
                Your payment proof has been submitted. Credits will be
                added after admin verification.
              </p>
            )}

            {purchase.status === 'payment_verified' && (
              <p style={{ color: '#86efac', lineHeight: 1.6 }}>
                Your payment has been verified.
              </p>
            )}

            {purchase.status === 'pending_payment' && (
              <p style={{ color: '#cbd5e1', lineHeight: 1.6 }}>
                Payment proof has not been submitted for this request.
              </p>
            )}

            <dl style={{ margin: '20px 0' }}>
              {rows.map(([label, value]) => (
                <div
                  key={label}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.5fr)',
                    gap: 16,
                    padding: '12px 0',
                    borderBottom: '1px solid rgba(148, 163, 184, 0.15)'
                  }}
                >
                  <dt style={{ color: '#94a3b8' }}>{label}</dt>
                  <dd
                    style={{
                      margin: 0,
                      textAlign: 'right',
                      overflowWrap: 'anywhere'
                    }}
                  >
                    {value ?? '—'}
                  </dd>
                </div>
              ))}
            </dl>

            {proof.rejectionReason && (
              <div
                style={{
                  padding: 16,
                  marginBottom: 18,
                  borderRadius: 12,
                  background: 'rgba(239, 68, 68, 0.1)'
                }}
              >
                <strong style={{ color: '#fca5a5' }}>
                  Rejection reason
                </strong>
                <p
                  style={{
                    marginBottom: 0,
                    whiteSpace: 'pre-wrap',
                    overflowWrap: 'anywhere'
                  }}
                >
                  {proof.rejectionReason}
                </p>
              </div>
            )}

            {proof.notes && (
              <div style={{ marginBottom: 20 }}>
                <h3 style={{ fontSize: 16 }}>Payment notes</h3>
                <p
                  style={{
                    color: '#cbd5e1',
                    whiteSpace: 'pre-wrap',
                    overflowWrap: 'anywhere'
                  }}
                >
                  {proof.notes}
                </p>
              </div>
            )}

            {canShowProof && (
              <div>
                <h3 style={{ fontSize: 16 }}>Payment proof</h3>
                <img
                  src={proofUrl}
                  alt="Submitted payment proof"
                  referrerPolicy="no-referrer"
                  style={{
                    display: 'block',
                    width: '100%',
                    maxHeight: 420,
                    objectFit: 'contain',
                    borderRadius: 12,
                    background: 'rgba(255, 255, 255, 0.04)'
                  }}
                />
              </div>
            )}
          </>
        ) : null}
      </div>
    </dialog>
  );
};

const Credits = () => {
  const navigate = useNavigate();
  const { token } = useAuth();

  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPurchaseId, setSelectedPurchaseId] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const closeDetails = useCallback(() => {
    setSelectedPurchaseId(null);
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const load = async () => {
      setLoading(true);
      setError('');

      try {
        const [walletData, purchasesData] = await Promise.all([
          request('/credits/wallet', token, controller.signal),
          request('/credits/purchases?limit=10', token, controller.signal)
        ]);

        if (controller.signal.aborted) return;

        setWallet(walletData.wallet);
        setTransactions(walletData.recentTransactions || []);
        setPurchases(
          (purchasesData.purchases || []).filter(purchase =>
            PURCHASE_STATUSES.has(purchase.status)
          )
        );
      } catch (err) {
        if (!controller.signal.aborted) {
          setError(err.message);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    load();

    return () => controller.abort();
  }, [token, reloadKey]);

  if (loading) {
    return (
      <div className="cr-page">
        <div className="cr-loading" role="status" aria-label="Loading wallet">
          <i className="fas fa-spinner fa-spin" aria-hidden="true" />
        </div>
      </div>
    );
  }

  return (
    <div className="cr-page">
      <div className="cr-header">
        <div>
          <h1>
            <i className="fas fa-wallet" aria-hidden="true" /> Credits & Wallet
          </h1>
          <p>Manage your credit balance and payment transactions</p>
        </div>

        <button
          type="button"
          className="cr-btn cr-btn-secondary"
          onClick={() => setReloadKey(value => value + 1)}
        >
          <i className="fas fa-sync-alt" aria-hidden="true" /> Refresh
        </button>
      </div>

      {error ? (
        <div className="cr-section" role="alert">
          <p style={{ color: '#ef4444' }}>{error}</p>
          <button
            type="button"
            className="cr-btn cr-btn-secondary"
            onClick={() => setReloadKey(value => value + 1)}
          >
            Try again
          </button>
        </div>
      ) : (
        <>
          <div className="cr-balance-grid">
            <CreditBalanceCard
              wallet={wallet}
              onBuy={() => navigate('/credits/buy')}
              onViewTransactions={() => navigate('/credits/transactions')}
            />
          </div>

          <div className="cr-section">
            <h3 className="cr-section-title">
              <i className="fas fa-receipt" aria-hidden="true" /> Recent Transactions
            </h3>

            {transactions.length === 0 ? (
              <div className="cr-empty">
                <i className="fas fa-inbox" aria-hidden="true" />
                <h3>No transactions yet</h3>
                <p>Buy credits or pay for a contract to get started</p>
              </div>
            ) : (
              <div className="cr-table-wrap">
                <table className="cr-table">
                  <thead>
                    <tr>
                      <th>Transaction</th>
                      <th>Type</th>
                      <th>Credits</th>
                      <th>Balance After</th>
                      <th>Status</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map(transaction => (
                      <tr key={transaction._id}>
                        <td>
                          <span className="cr-tx-id">
                            {transaction.transactionId}
                          </span>
                        </td>
                        <td>{readable(transaction.type)}</td>
                        <td>
                          <span
                            className={
                              transaction.credits > 0
                                ? 'cr-tx-positive'
                                : 'cr-tx-negative'
                            }
                          >
                            {transaction.credits > 0 ? '+' : ''}
                            {transaction.credits}
                          </span>
                        </td>
                        <td>{transaction.balanceAfter}</td>
                        <td>
                          <StatusBadge status={transaction.status} />
                        </td>
                        <td>{formatDate(transaction.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="cr-section">
            <h3 className="cr-section-title">
              <i className="fas fa-shopping-bag" aria-hidden="true" /> Purchases
            </h3>

            <p
              style={{
                fontSize: 13,
                opacity: 0.75,
                marginBottom: 18,
                lineHeight: 1.6
              }}
            >
              Pending and submitted payment requests appear here. Resume a
              pending purchase to upload your payment proof. Credits are added
              to your wallet only after payment verification.
            </p>

            {purchases.length === 0 ? (
              <div className="cr-empty">
                <i className="fas fa-inbox" aria-hidden="true" />
                <h3>No purchases yet</h3>
                <p>
                  Start a credit purchase and it will appear here so you can
                  resume at any time.
                </p>
              </div>
            ) : (
              <div className="cr-table-wrap">
                <table className="cr-table">
                  <thead>
                    <tr>
                      <th>Purchase ID</th>
                      <th>Credits</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchases.map(purchase => (
                      <tr key={purchase._id}>
                        <td>
                          <span className="cr-tx-id">
                            {purchase.purchaseId}
                          </span>
                        </td>
                        <td>{purchase.credits}</td>
                        <td>{formatAmount(purchase)}</td>
                        <td>
                          <StatusBadge status={purchase.status} purchase />
                        </td>
                        <td>{formatDate(purchase.createdAt)}</td>
                        <td>
                          {purchase.status === 'pending_payment' ? (
                            <button
                              type="button"
                              className="cr-btn cr-btn-primary"
                              onClick={() =>
                                navigate(`/credits/buy?resume=${purchase._id}`)
                              }
                              aria-label={`Resume purchase ${purchase.purchaseId}`}
                              style={{
                                padding: '6px 12px',
                                fontSize: 12
                              }}
                            >
                              <i className="fas fa-arrow-right" aria-hidden="true" /> Continue
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="cr-btn cr-btn-secondary"
                              onClick={() => setSelectedPurchaseId(purchase._id)}
                              aria-label={`View purchase ${purchase.purchaseId}`}
                              style={{
                                padding: '6px 12px',
                                fontSize: 12
                              }}
                            >
                              <i className="fas fa-eye" aria-hidden="true" /> View
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {selectedPurchaseId && (
        <PurchaseDetails
          key={selectedPurchaseId}
          purchaseId={selectedPurchaseId}
          token={token}
          onClose={closeDetails}
        />
      )}
    </div>
  );
};

export default Credits;